import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TransportType } from '../types';

export const TRANSPORT_MODES: { value: TransportType; label: string }[] = [
  { value: 'van', label: '🚐 Van' },
  { value: 'tourbus', label: '🚌 Tourbus' },
  { value: 'plane', label: '✈️ Plane' },
  { value: 'train', label: '🚆 Train' },
];

export function transportLabel(mode: TransportType): string {
  return TRANSPORT_MODES.find((m) => m.value === mode)?.label ?? mode;
}

interface PopulateFromProps {
  /** The currently active transport mode (excluded from the choices). */
  current: TransportType;
  /** Called with the chosen source mode. */
  onSelect: (mode: TransportType) => void;
  /** Button label. Defaults to "Populate from". */
  label?: string;
  /** Compact = icon-only button (for inline per-field use). */
  compact?: boolean;
  className?: string;
  /**
   * Freeze the control (lot A2-CAP): the caller cannot write, so filling a
   * field from another transport mode is impossible. Defaults to false — a
   * normal editor is unaffected.
   */
  disabled?: boolean;
}

/**
 * Reusable "Populate from" control. Renders a button that opens a dropdown
 * listing every transport mode EXCEPT the current one. Selecting a mode calls
 * `onSelect(mode)`. Used both at page level (fill all fields) and inline
 * (per-field) — the parent decides what to do (and whether to confirm).
 */
export default function PopulateFrom({
  current,
  onSelect,
  label,
  compact = false,
  className,
  disabled = false,
}: PopulateFromProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const labelText = label ?? t('config.populateFrom');

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const choices = TRANSPORT_MODES.filter((m) => m.value !== current);

  return (
    <div className={`populate-from ${className ?? ''}`} ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className={compact ? 'btn btn-ghost btn-small' : 'btn btn-secondary btn-small'}
        onClick={() => setOpen((o) => !o)}
        title={t('config.populateFromTitle')}
        disabled={disabled}
      >
        {compact ? '📥' : `⤓ ${labelText}`}
      </button>
      {open && !disabled && (
        <div className="populate-from-menu" style={{
          position: 'absolute',
          zIndex: 50,
          top: 'calc(100% + 4px)',
          left: 0,
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: 4,
          minWidth: 150,
          boxShadow: 'var(--shadow-lg)',
        }}>
          {choices.map((m) => (
            <button
              key={m.value}
              type="button"
              className="populate-from-item"
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                background: 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text)',
                cursor: 'pointer',
                fontSize: 'var(--font-sm)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              onClick={() => {
                setOpen(false);
                onSelect(m.value);
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}